/**
 * Runtime localisation for the Grammar of Use microsite.
 *
 * The microsite is a self-extracting HTML bundle rendered in an iframe
 * (src/pages/AngelInsider/grammar-of-use-v1-PPS.html), so its copy never
 * passes through react-intl and cannot use <FormattedMessage>. Instead we
 * walk the rendered document and swap text nodes against a map keyed by the
 * English source string — the same keying used by the extracted message maps
 * in ../data/grammarOfUse/<locale>.json.
 *
 * Only same-origin documents can be localised this way. When an organisation
 * has `userMeta.micrositeUrl` set, the iframe loads a remote copy on another
 * origin: contentDocument is unreachable, applyGrammarOfUseCopy bails out, and
 * that copy has to be translated in the microsite's own source project. The
 * bundled fallback is also a generated artefact, so re-running the bundler
 * discards nothing here — the translations live in the JSON maps, not in the
 * bundle.
 */

/** Attributes whose values are user-visible and therefore worth translating. */
const TRANSLATABLE_ATTRS = ['alt', 'title', 'placeholder', 'aria-label'] as const;

/** Never rewrite anything inside these — their text is code, not copy. */
const OPAQUE_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);

export type CopyMap = Record<string, string>;

/**
 * Replaces a node's text with its translation, preserving the surrounding
 * whitespace: the extracted keys are trimmed, but the template relies on the
 * original padding for inline runs (e.g. "…use " + <b>Chat</b>).
 */
const translateText = (value: string, copy: CopyMap): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const hit = copy[trimmed];
  if (hit === undefined || hit === trimmed) return undefined;
  const [, lead = '', , trail = ''] = /^(\s*)([\s\S]*?)(\s*)$/.exec(value) ?? [];
  return `${lead}${hit}${trail}`;
};

/** Walks `root`, translating text nodes and visible attributes in place. */
export const applyCopyToDocument = (root: Document | Element, copy: CopyMap): number => {
  const doc = 'ownerDocument' in root && root.ownerDocument ? root.ownerDocument : root;
  let applied = 0;

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement && OPAQUE_TAGS.has(node.parentElement.tagName)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  const pending: Array<[Text, string]> = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const next = translateText((n as Text).data, copy);
    if (next !== undefined) pending.push([n as Text, next]);
  }
  for (const [node, next] of pending) {
    node.data = next;
    applied++;
  }

  for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
    for (const attr of TRANSLATABLE_ATTRS) {
      const current = el.getAttribute(attr);
      if (current === null) continue;
      const next = translateText(current, copy);
      if (next !== undefined) {
        el.setAttribute(attr, next);
        applied++;
      }
    }
  }

  return applied;
};

/**
 * Localises a Grammar of Use iframe once its bundle has finished unpacking.
 *
 * The bundle renders asynchronously well after the iframe's load event, and
 * keeps mutating while assets resolve, so a single pass would miss most of the
 * copy. We translate on every mutation until the document settles, then stop —
 * `stopAfterMs` bounds the observer so a bundle that never settles cannot leak
 * one. Returns a disposer so callers can cancel on unmount.
 */
export const localizeGrammarOfUseFrame = (
  frame: HTMLIFrameElement,
  copy: CopyMap,
  { stopAfterMs = 15000 }: { stopAfterMs?: number } = {},
): (() => void) => {
  let doc: Document | null = null;
  try {
    // Cross-origin (remote microsite) throws or yields null — nothing to do.
    doc = frame.contentDocument;
  } catch {
    return () => {};
  }
  if (!doc?.body) return () => {};

  const target = doc;
  applyCopyToDocument(target, copy);

  const observer = new MutationObserver(() => applyCopyToDocument(target, copy));
  observer.observe(target.body, { childList: true, subtree: true, characterData: true });
  const timer = setTimeout(() => observer.disconnect(), stopAfterMs);

  return () => {
    clearTimeout(timer);
    observer.disconnect();
  };
};
